import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, LogOut, Menu, X, Printer } from "lucide-react";
import { SLIDES } from "@/deck/slides";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { SlideContext } from "@/deck/SlideContext";

export default function DeckShell({ session, onLogout }) {
  const [idx, setIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const total = SLIDES.length;
  const enteredAtRef = useRef(Date.now());
  const prevIdxRef = useRef(0);

  const flushView = useCallback((slideIdx, label, durationMs) => {
    if (!session?.session_id) return;
    // fire-and-forget
    api.post("/session/track", {
      session_id: session.session_id,
      slide_idx: slideIdx,
      slide_label: label,
      duration_ms: durationMs,
    }).catch(() => {});
  }, [session]);

  // Track first slide view on mount
  useEffect(() => {
    enteredAtRef.current = Date.now();
    flushView(0, SLIDES[0].label, 0);
    // On unload flush time on current slide
    const onUnload = () => {
      const dur = Date.now() - enteredAtRef.current;
      try {
        const data = JSON.stringify({
          session_id: session?.session_id,
          slide_idx: prevIdxRef.current,
          slide_label: SLIDES[prevIdxRef.current].label,
          duration_ms: dur,
        });
        if (navigator.sendBeacon) {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/session/track`;
          navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = useCallback((n) => {
    const target = Math.max(0, Math.min(total - 1, n));
    setIdx((prev) => {
      if (target === prev) return prev;
      const dur = Date.now() - enteredAtRef.current;
      flushView(prev, SLIDES[prev].label, dur);
      prevIdxRef.current = target;
      enteredAtRef.current = Date.now();
      // post the entry to new slide with duration 0 so we also count the view
      flushView(target, SLIDES[target].label, 0);
      return target;
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [total, flushView]);

  const next = useCallback(() => go(idx + 1), [go, idx]);
  const prev = useCallback(() => go(idx - 1), [go, idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Home") go(0);
      else if (e.key === "End") go(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go, total]);

  const Current = SLIDES[idx].component;
  const meta = SLIDES[idx];
  const partnerName = session?.partner_name;

  return (
    <div className="relative min-h-screen w-full bg-background text-foreground">
      <header className="no-print fixed top-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-6 md:px-10 h-14">
          <div className="flex items-center gap-3 min-w-0">
            <LogoMark className="h-6 w-auto flex-shrink-0" />
            {partnerName ? (
              <span
                data-testid="partner-badge"
                className="hidden md:inline-flex items-center gap-2 ml-2 px-2.5 py-1 border border-primary text-primary text-[10px] uppercase tracking-brand font-bold"
              >
                <span className="w-1 h-1 bg-primary rounded-full" />
                For {partnerName}
              </span>
            ) : (
              <span className="hidden md:inline text-xs text-muted-foreground font-mono-data truncate">
                · {meta.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-5 text-xs font-mono-data">
            <span data-testid="slide-counter" className="tabular">
              {idx === 0 ? "COVER" : String(idx).padStart(2, "0")} <span className="text-muted-foreground">/ {String(total - 1).padStart(2, "0")}</span>
            </span>
            <button data-testid="open-menu" onClick={() => setMenuOpen(true)} className="hover:text-primary transition-colors" aria-label="Open menu">
              <Menu className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button data-testid="print-deck" onClick={() => window.print()} className="hidden md:inline-flex hover:text-primary transition-colors" aria-label="Print">
              <Printer className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button data-testid="logout-button" onClick={onLogout} className="hidden md:inline-flex hover:text-primary transition-colors" aria-label="Logout">
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
        <div className="h-[2px] bg-border">
          <motion.div
            className="h-full bg-primary"
            animate={{ width: `${((idx + 1) / total) * 100}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </header>

      <main className="pt-14 pb-20 min-h-screen">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="slide"
            data-testid={`slide-${idx}`}
          >
            <SlideContext.Provider value={{ idx, total, label: meta.label, tag: "" }}>
              <Current goTo={go} session={session} />
            </SlideContext.Provider>
          </motion.div>
        </AnimatePresence>
      </main>

      <nav className="no-print fixed bottom-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-sm border-t border-border">
        <div className="flex items-center justify-between px-6 md:px-10 h-14">
          <button
            data-testid="prev-slide"
            onClick={prev}
            disabled={idx === 0}
            className="btn-sharp border border-foreground/30 hover:border-foreground hover:bg-foreground hover:text-background disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-[11px] uppercase tracking-brand text-muted-foreground hidden md:inline">
            Use ← → keys to navigate
          </span>
          <button
            data-testid="next-slide"
            onClick={next}
            disabled={idx === total - 1}
            className="btn-primary disabled:opacity-30"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-foreground/40 no-print"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-background border-l border-foreground no-print overflow-y-auto"
              data-testid="slide-menu"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-foreground">
                <div>
                  <span className="overline">Deck Index</span>
                  {partnerName && (
                    <p className="font-display text-lg mt-1">{partnerName}</p>
                  )}
                </div>
                <button onClick={() => setMenuOpen(false)} aria-label="Close menu">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <ol className="divide-y divide-border">
                {SLIDES.map((s, i) => (
                  <li key={i}>
                    <button
                      data-testid={`menu-goto-${i}`}
                      onClick={() => { go(i); setMenuOpen(false); }}
                      className={`w-full text-left flex items-baseline gap-5 px-6 py-4 hover:bg-muted transition-colors ${i === idx ? "bg-muted" : ""}`}
                    >
                      <span className="font-mono-data text-xs text-muted-foreground">
                        {i === 0 ? "00" : String(i).padStart(2, "0")}
                      </span>
                      <span className="font-display text-lg">{s.label}</span>
                    </button>
                  </li>
                ))}
              </ol>
              <div className="px-6 py-5 border-t border-foreground flex items-center justify-between text-xs">
                <button onClick={onLogout} className="flex items-center gap-2 hover:text-primary">
                  <LogOut className="w-3.5 h-3.5" /> Log out
                </button>
                <span className="font-mono-data text-muted-foreground">CONFIDENTIAL</span>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
